/*
  Life Inbox — dedicated page. Hero input + recent captures list.
*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Inbox, Pill, CheckSquare, ShoppingCart, StickyNote, Bell,
  CalendarDays, Trash2, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import LifeInboxInput from "../components/LifeInboxInput";
import { inboxApi } from "../lib/inboxApi";

const TYPE_ICON = {
  task: CheckSquare, reminder: Bell, shopping: ShoppingCart, note: StickyNote,
  prescription: Pill, event: CalendarDays,
};

export default function InboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("new");

  async function load() {
    setLoading(true);
    try {
      const data = await inboxApi.list({ status: filter === "all" ? undefined : filter, limit: 100 });
      setItems(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [filter]);

  async function dismiss(id) {
    try {
      await inboxApi.update(id, { status: "dismissed" });
      setItems(xs => xs.filter(x => x.id !== id));
      toast.success("Dismissed");
    } catch { toast.error("Couldn't dismiss"); }
  }

  async function remove(id) {
    try {
      await inboxApi.remove(id);
      setItems(xs => xs.filter(x => x.id !== id));
    } catch { toast.error("Couldn't delete"); }
  }

  return (
    <div className="animate-fade-up max-w-2xl">
      <div className="mb-6">
        <span className="label-caps">Your capture feed</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Life Inbox.</h1>
        <p className="mt-2 text-sm sm:text-base" style={{ color: "var(--text-muted)" }}>
          One input. We sort it into the right tool.
        </p>
      </div>

      <LifeInboxInput />

      <div className="mt-8 mb-3 flex items-center justify-between">
        <div className="flex gap-1">
          {[
            ["new", "New"],
            ["routed", "Routed"],
            ["dismissed", "Dismissed"],
            ["all", "All"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "text-white border-transparent" : "hover:border-[color:var(--primary)]"}`}
              style={filter === k ? { background: "var(--primary)" } : { borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            >{l}</button>
          ))}
        </div>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>{items.length} items</span>
      </div>

      {loading ? (
        <div className="text-sm text-center py-12" style={{ color: "var(--text-muted)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-tactile rounded-2xl p-10 text-center">
          <Inbox className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Nothing here yet. Capture anything above.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="inbox-list">
          {items.map(it => {
            const Icon = TYPE_ICON[it.type] || Sparkles;
            const when = it.datetime ? new Date(it.datetime).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null;
            return (
              <li key={it.id} className="card-tactile rounded-xl p-3 flex items-start gap-3" data-testid="inbox-item">
                <div className="h-9 w-9 rounded-lg shrink-0 bg-[color:var(--primary-soft)] flex items-center justify-center">
                  <Icon className="h-4 w-4 text-[color:var(--primary-ink)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-ink)" }}>{it.type}</span>
                    <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{new Date(it.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="font-semibold text-sm mt-0.5 truncate">{it.title}</div>
                  {when && <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>{when}</div>}
                  {it.original && it.original !== it.title && (
                    <div className="text-[11px] italic mt-1 truncate" style={{ color: "var(--text-muted)" }}>"{it.original}"</div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {it.status === "new" && (
                    <button onClick={() => dismiss(it.id)} className="p-1.5 rounded hover:bg-[color:var(--surface-2)]" title="Dismiss">
                      <CheckCheck className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                    </button>
                  )}
                  <button onClick={() => remove(it.id)} className="p-1.5 rounded hover:bg-[color:var(--surface-2)]" title="Delete">
                    <Trash2 className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-8 pt-6 border-t text-sm" style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}>
        <span>
          Captures are classified by Gemini 3 Flash. Your raw text is never sent to ads or analytics —
          <Link to="/privacy" className="underline hover:text-[color:var(--primary-ink)]"> read privacy</Link>.
        </span>
      </div>
    </div>
  );
}
